import React, { useEffect, useState, useMemo } from "react";
import { components } from "react-select";
import "react-tabs/style/react-tabs.css";
import styles from "./index.module.css";
import FormFields from "../../../components/Form";
import ContainerHeader from "common/ContainerHeader";
import {
  getData,
  GetTicketBody,
  postData,
  SearchOptions,
  updateData,
} from "utils/queries";
import Tabs_ from "common/Tabs";
import PaidTicket from "components/Ticket/Paid";
import FreeTicket from "components/Ticket/Free";
import { useNavigate, useLocation } from "react-router-dom";
import Toast, { formatDateTimeRange, getDateTimeFromDate } from "utils";

const CreateTicket = ({ data, ticket_id, edit = false }) => {
  const [ticketDetails, setTicketDetails] = useState({});
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [ticketNames, setTicketNames] = useState([]);
  const [variantTicketId, setVariantTicketId] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [persistentFields, setPersistentFields] = useState({
    ticket_name: null,
    event_name: null,
    variant_type: null,
    image_url: null,
  });
  const [parentTicketData, setParentTicketData] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Get parentTicketId / duplicateVariantId from URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const parentId = queryParams.get('parentTicketId');
    const duplicateVariantId = queryParams.get('duplicateVariantId');

    if (duplicateVariantId) {
      fetchDuplicateVariantData(duplicateVariantId);
    } else if (parentId) {
      fetchParentTicketData(parentId);
      setVariantTicketId(parentId);
    }
  }, [location]);

  // Function to fetch parent ticket data
  const fetchParentTicketData = async (parentId) => {
    
    try {
      const result = await getData(`ticket/${parentId}`);
      
      if (result?.data) {
        
        const parentData = result.data[0];
        
        // Set form to the correct tab based on parent ticket type
        setSelectedTabIndex(parentData?.ticket_type === "paid" ? 0 : 1);
        // setSelectedTabIndex( selectedTabIndex === 0 ? "paid" : "free")


        
        // Prefill fields from parent ticket
        setPersistentFields({
          ticket_name: parentData.ticket_name,
          event_name: { 
            label: parentData.event_name, 
            value: parentData.event_id,
            booking_fee_percentage: parentData.booking_fee_percentage
          },
          variant_type: "variant",
          image_url: parentData.image_url,
        });
        
        // Set parent ticket data
        setParentTicketData(parentData);
        
        // Initialize ticket details with parent data
        setTicketDetails({
          ticket_name: { value: parentData.ticket_name, label: parentData.ticket_name },
          event_name: { 
            label: parentData.event_name, 
            value: parentData.event_id,
            booking_fee_percentage: parentData.booking_fee_percentage
          },
          variant_type: "variant",
          image_url: parentData.image_url,
          variant_name: null // This will be set by user for the new variant
        });
      }
    } catch (error) {
      console.error("Error fetching parent ticket:", error);
      Toast().Error("Failed to load parent ticket data");
    }
  };

  // Fetch a variant and prefill the form with its data so the user can save it as a brand new variant.
  // variant_name is left empty (user must provide a unique one) and sales counters are reset.
  const fetchDuplicateVariantData = async (variantId) => {
    try {
      const result = await getData(`ticket/tickets/${variantId}`);
      const Data = result?.data?.[0];
      if (!Data) return;

      const eventResult = await getData(`event/events/${Data.event_id}`);
      const eventData = eventResult?.data?.data?.events;
      const percentage = eventData?.booking_fee_percentage ?? 10;

      const price = Number(Data.price) || 0;
      const calculatedFee = parseFloat((price * (percentage / 100)).toFixed(2));
      const customer_pays = parseFloat((price + calculatedFee).toFixed(2));

      const { date: startDate } = getDateTimeFromDate(Data.on_sale_from);
      const { date: endDate } = getDateTimeFromDate(Data.on_sale_to);
      const updatedDateAndTime = formatDateTimeRange(Data.on_sale_from, Data.on_sale_to);

      const eventNameOption = {
        label: Data.event_name,
        value: Data.event_id,
        booking_fee_percentage: percentage,
        installment_plans: eventData?.installment_plans || [],
        installments_enabled: eventData?.installments_enabled,
        installment_min_amount: eventData?.installment_min_amount,
        installment_cutoff_days: eventData?.installment_cutoff_days,
      };

      setSelectedTabIndex(Data?.ticket_type === "paid" ? 0 : 1);
      // The duplicate goes under the same parent ticket as the source variant
      setVariantTicketId(Data.ticket_id);
      setPersistentFields({
        ticket_name: Data.ticket_name,
        event_name: eventNameOption,
        variant_type: "variant",
        image_url: Data.image_url,
      });

      // Strip identifiers so the backend creates a new variant; everything else carries over.
      const { id, ticket_id: _parentId, ...rest } = Data;

      const variantPayload = {
        ...rest,
        booking_fee: calculatedFee,
        customer_pays,
        you_receive: price,
        installment_min_amount: parseFloat(Data?.installment_min_amount ?? 0),
        installment_plans: (Data?.installment_plans || []).map(plan =>
          typeof plan === 'string'
            ? { label: plan.replace("installment_", "") + " Months", value: plan }
            : plan
        ),
        variant_name: Data.variant_name || null,
        on_sale_from: startDate,
        startHours: {
          label: updatedDateAndTime.startHours.label,
          value: updatedDateAndTime.startHours.value,
        },
        startMinutes: {
          label: updatedDateAndTime.startMinutes.label,
          value: updatedDateAndTime.startMinutes.value,
        },
        currency_type: typeof Data.currency_type === 'string'
          ? JSON.parse(Data.currency_type)
          : Data.currency_type,
        on_sale_to: endDate,
        endHours: {
          label: updatedDateAndTime.endHours.label,
          value: updatedDateAndTime.endHours.value,
        },
        endMinutes: {
          label: updatedDateAndTime.endMinutes.label,
          value: updatedDateAndTime.endMinutes.value,
        },
      };

      setTicketDetails({
        ticket_name: { value: Data.ticket_name, label: Data.ticket_name },
        event_name: eventNameOption,
        variant_type: "variant",
        image_url: Data.image_url,
        variant_name: Data.variant_name
          ? { value: Data.variant_name, label: Data.variant_name }
          : null,
        // Store under both keys: `undefined` (read by the create-flow save handler)
        // and the variant name (read by the form renderer via ticketDetails[variant_name.value]).
        undefined: variantPayload,
        ...(Data.variant_name ? { [Data.variant_name]: variantPayload } : {}),
      });
    } catch (error) {
      console.error("Error fetching variant for duplicate:", error);
      Toast().Error("Failed to load variant data");
    }
  };

  useEffect(() => {
    // ticket_name may be either a string (after user types in the text input) or
    // an object { value, label } (when prefilled by edit/duplicate flows).
    const ticketNameStr =
      typeof ticketDetails?.ticket_name === 'string'
        ? ticketDetails.ticket_name
        : ticketDetails?.ticket_name?.value;

    if (
      ticketNameStr ||
      ticketDetails?.event_name ||
      ticketDetails?.variant_type ||
      ticketDetails?.image_url
    ) {
      setPersistentFields({
        ticket_name: ticketNameStr,
        event_name: ticketDetails.event_name,
        variant_type: ticketDetails.variant_type,
        image_url: ticketDetails.image_url
      });
    }
  }, [
    ticketDetails?.ticket_name,
    ticketDetails?.event_name,
    ticketDetails?.variant_type,
    ticketDetails?.image_url
  ]);

  const handleSetTicketDetails = (variant, values) => {
    setTicketDetails((oldValue) => {
      const prevVariantData = oldValue[variant] || {};
      const newVariantData = typeof values === 'function' ? values(prevVariantData) : values;
      return {
        ...oldValue,
        [variant]: newVariantData,
      };
    });
  };

  const handleSetSelectedTabIndex = (val) => {
    // Don't allow changing tabs if creating a variant for existing ticket
    if (variantTicketId && !edit) {
      Toast().Warning("Cannot change ticket type when adding a variant");
      return;
    }
    
    setSelectedTabIndex(val);
    setTicketDetails({});
    setPersistentFields({ 
      ticket_name: null,
      event_name: null,
      variant_type: null,
      image_url: null,
    });
  };

  useEffect(() => {
    if (data) {
      const variantKey = data.variant_name?.value || data.variant_name || "undefined";
      const migratedData = {
        ...data,
        [variantKey]: {
          ...(data[variantKey] || data.undefined),
          variant_name: data.variant_name?.value || data.variant_name
        }
      };
      setTicketDetails(migratedData);
      setTicketNames([data?.variant_name]);
      setSelectedTabIndex(data?.ticket_type?.value === "paid" ? 0 : 1);
      setVariantTicketId(data?.parent_ticketID);
      setPersistentFields({
        ticket_name: data.ticket_name?.value,
        event_name: data.event_name,
        variant_type: data.variant_type,
        image_url: data.image_url
      });
    }
  }, [data]);

  const handleChange = (label, parseType) => (e) => {
    let value = e?.target?.value ?? e;
    if (parseType) {
      value = parseType(e.target?.type === "checkbox" ? e : value);
    }

    if (label === "variant_name") {
      const oldVariantName = ticketDetails.variant_name?.value;
      setTicketDetails(prev => {
        const newDetails = { ...prev };
        if (oldVariantName && newDetails[oldVariantName]) {
          newDetails[value.value] = {
            ...newDetails[oldVariantName],
            variant_name: value.value
          };
          delete newDetails[oldVariantName];
        }
        return {
          ...newDetails,
          [label]: value,
        };
      });
    } else {
      setTicketDetails(prev => ({
        ...prev,
        [label]: value,
      }));
    }

    if (["ticket_name", "event_name", "variant_type", "image_url"].includes(label)) {
      setPersistentFields(prev => ({
        ...prev,
        [label]: value,
      }));
    }
  };

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all upcoming events when no search term
        optionsResult = await SearchOptions(`event/events?eventType=upcoming&limit=100&offset=0&currentDate=${new Date().toISOString()}`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
          starts: e?.starts,
          ends: e?.ends,
          booking_fee_percentage: e?.booking_fee_percentage,
          installments_enabled: e?.installments_enabled,
          installment_plans: e?.installment_plans,
          installment_min_amount: e?.installment_min_amount,
          installment_cutoff_days: e?.installment_cutoff_days,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data
          ?.filter((e) =>
            e?.event_name?.toLowerCase().includes(search.toLowerCase())
          )
          ?.map((e) => ({
            value: e?.id,
            label: e?.event_name,
            starts: e?.starts,
            ends: e?.ends,
            booking_fee_percentage: e?.booking_fee_percentage,
            installments_enabled: e?.installments_enabled,
            installment_plans: e?.installment_plans,
            installment_min_amount: e?.installment_min_amount,
            installment_cutoff_days: e?.installment_cutoff_days,
            asfdasdf: "asdfasdf",
          })) || [];
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  const CustomOption = (props) => {
    if (props?.data?.__isNew__) {
      return (
        <components.Option {...props}>
          <div style={{ color: "blue" }}>
            <span style={{ marginRight: "10px" }}>➕</span>
            <span>
              <strong>{props.data.label}</strong>
            </span>
          </div>
        </components.Option>
      );
    }

    return (
      <components.Option {...props}>{props?.data?.label}</components.Option>
    );
  };

  const handleCreate = (inputValue) => {
    setTicketNames((val) => [...val, { label: inputValue, value: inputValue }]);
  };

  console.log("ticketDetails", ticketDetails);
  const formFields = useMemo(() => {
    const commonFields = [
      {
        label: "Event Name",
        id: "event_name",
        placeholder: "Event Name",
        isDisabled: variantTicketId || ticket_id ? true : false,
        loadOptions: fetchOptions,
        isSearchable: true,
        type: "select_search",
        value: persistentFields?.event_name || ticketDetails?.event_name,
        // value: typeof persistentFields?.event_name === 'string' 
        // ? { label: persistentFields.event_name, value: ticketDetails?.event_name?.value || '' }
        // : persistentFields?.event_name || ticketDetails?.event_name,
        required: true,
        loading: true,
        onChange: handleChange("event_name"),
        onSelect: (e) => {
          console.log("adfadsf", e);
        },
      },
      {
        parentLabel: "Ticket Image",
        label:
          "Events with images are noticed by more customers, and usually sell more tickets.",
        id: "image_url",
        type: "image",
        isDisabled: variantTicketId && !edit ? true : false,
        value: ticketDetails?.image_url || persistentFields?.image_url,
        required: true,
        onChange: handleChange("image_url"),
      },
      {
        label: "Ticket Type",
        id: "variant_type",
        placeholder: "Ticket Type",
        type: "radio",
        disabled: ticket_id || variantTicketId ? true : false,
        radioClasses: {
          display: "inline-flex",
          alignItems: "center",
        },
        selectedValue:
          persistentFields?.variant_type || ticketDetails?.variant_type || (variantTicketId ? "variant" : null),
        options: [
          { label: "Individual", value: "individual" },
          { label: "Variant", value: "variant" },
        ],
        onChange: handleChange("variant_type"),
      },
    ];

    // If we have a parent ticket or creating a variant, force variant type
    if (variantTicketId && !edit) {
      commonFields.find(field => field.id === "variant_type").selectedValue = "variant";
    }

    // Both ticket_name and variant_name can hold either a string (typed in the text input)
    // or an object { value, label } (prefilled by edit/duplicate flows).
    const stringFromMaybeObject = (v) =>
      typeof v === 'string' ? v : v?.value;

    if (ticketDetails?.variant_type === "variant" || (variantTicketId && !edit)) {
      commonFields.push({
        label: "Ticket Name",
        id: "ticket_name",
        placeholder: "Enter Ticket Name",
        type: "text",
        isDisabled: ticket_id ? true : false,
        value:
          stringFromMaybeObject(persistentFields?.ticket_name) ||
          stringFromMaybeObject(ticketDetails?.ticket_name) ||
          parentTicketData?.ticket_name,
        required: true,
        onChange: handleChange("ticket_name"),
      });

      commonFields.push({
        label: "Variant Name",
        id: "variant_name",
        placeholder: "Select Existing or Create New Variant",
        type: "text",
        isDisabled: ticket_id ? true : false,
        value: stringFromMaybeObject(ticketDetails?.variant_name),
        required: true,
        onChange: handleChange("variant_name"),
      });
    }

    if (ticketDetails?.variant_type === "individual") {
      commonFields.push({
        label: "Ticket Name",
        id: "ticket_name",
        placeholder: "Enter Ticket Name",
        type: "text",
        isDisabled: ticket_id ? true : false,
        value:
          stringFromMaybeObject(persistentFields?.ticket_name) ||
          stringFromMaybeObject(ticketDetails?.ticket_name),
        required: true,
        onChange: handleChange("ticket_name"),
      });
    }

    return commonFields;
  }, [
    ticketDetails?.variant_type,
    persistentFields?.ticket_name,
    ticketNames,
    persistentFields?.event_name,
    ticketDetails?.variant_name,
    ticketDetails?.image_url,
  ]);

  console.log("ticketDetails", ticketDetails);

  const resetFormWithPreservedFields = () => {
    const resetState = {
      ...persistentFields,
      variant_name: null,
      // image_url: "",
      undefined: {
        subtitle_description: "",
        quantity_available: "",
        min_tickets_per_order: "",
        max_tickets_per_order: "",
        startHours: {},
        startMinutes: {},
        endHours: {},
        endMinutes: {},
        price: "",
        you_receive: "",
        booking_fee: "",
        customer_pays: "",
        enable_discount_codes: true,
      },
    };

    setTicketDetails(resetState);
    setFormKey((prev) => prev + 1);
  };

 
  const duplicateFormWithPreservedFields = () => {
    const currentVariantName = ticketDetails?.variant_name;
    const sourceData =
      (currentVariantName?.value && ticketDetails[currentVariantName.value]) ||
      ticketDetails.undefined ||
      {};

    const { id, ...rest } = sourceData;

    const variantPayload = {
      ...rest,
      variant_name: currentVariantName?.value || null,
    };

    const duplicateState = {
      ...persistentFields,
      variant_name: currentVariantName || null,
      // Mirror under both keys so the form renderer (ticketDetails[variant_name.value])
      // and the create-flow save handler (ticketDetails.undefined) both see it.
      undefined: variantPayload,
      ...(currentVariantName?.value
        ? { [currentVariantName.value]: variantPayload }
        : {}),
    };

    setTicketDetails(duplicateState);
    setFormKey((prev) => prev + 1);
  };

  const handleSaveTicket = async (e, isVariant = false, isDuplicate = false) => {
    e?.preventDefault();
    const ticket_type = selectedTabIndex === 0 ? "paid" : "free";
    const hasIndividual = ticketDetails?.variant_type === "individual"; 

  
    try {
      // Extract simple value for variant_name to prevent nesting
      const simpleVariantName = 
        typeof ticketDetails?.variant_name === 'object' 
          ? ticketDetails.variant_name.value 
          : ticketDetails?.variant_name;
      const simpleTicketName =
        typeof ticketDetails?.ticket_name === "object"
          ? ticketDetails.ticket_name.value
          : ticketDetails?.ticket_name;

      const topBody = {
        ticket_type,
        ticket_name: simpleTicketName,
        image_url: ticketDetails?.image_url,
        variant_type: ticketDetails?.variant_type,
        event_id: ticketDetails?.event_name?.value,
        variant_name: simpleVariantName
      };

      // const currentVariantName = 
      // typeof ticketDetails?.variant_name === 'object' 
      //   ? ticketDetails.variant_name.value 
      //   : ticketDetails?.variant_name;
      let variantData = null;
      const currentVariantName = ticketDetails?.variant_name?.value;
      if (ticket_id) {
        variantData = currentVariantName 
          ? ticketDetails[currentVariantName]
          : ticketDetails.undefined;

        if (!variantData) {
          console.error("Variant data missing:", ticketDetails);
          return Toast().Error("Variant data is missing");
        }

        const processedVariant = GetTicketBody({
          ...variantData,
          variant_name: simpleVariantName,
          ticket_name: simpleTicketName,
          ticket_image: ticketDetails?.image_url,
          id: variantData.id
        });

        const result = await updateData(`ticket/tickets/${ticket_id}`, {
          ...topBody,
          variants: [{
            ...processedVariant,
            variant_name: hasIndividual ? simpleTicketName : simpleVariantName,
          }],
        });

        // if (result?.status === 200) {
        //   navigate("/ticket");
        // }
        if(isVariant){
          setVariantTicketId(result?.data?.data?.id);
          if (isDuplicate) {
            duplicateFormWithPreservedFields();
          } else {
            resetFormWithPreservedFields();
          }
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          navigate("/ticket");
        }
        return;
      }

      const variantSlot =
        (simpleVariantName && ticketDetails?.[simpleVariantName]) ||
        ticketDetails?.undefined ||
        {};

      let variants;
      if (ticketDetails?.variant_type === "individual") {
        variants = GetTicketBody({
          ...variantSlot,
          variant_name: simpleVariantName || null,
          ticket_name: ticketDetails?.ticket_name,
          ticket_image: ticketDetails?.image_url,
          enable_discount_codes: variantSlot?.enable_discount_codes ?? true, // Ensure true
        });
      } else if (ticketDetails?.variant_type === "variant") {
        let body = {
          ...variantSlot,
          variant_name: simpleVariantName || null,
          ticket_name: ticketDetails?.ticket_name,
          ticket_image: ticketDetails?.image_url,
          enable_discount_codes: variantSlot?.enable_discount_codes ?? true, // Ensure true
        };

        if (variantTicketId) {
          body.ticket_id = variantTicketId;
        }

        variants = GetTicketBody(body);
      }

      if (variants) {
        variants.installment_min_amount = parseFloat(variants.installment_min_amount ?? 0);
      }

      const result = await postData("ticket/tickets", {
        ...topBody,
        variants: [variants],
      });

      if (ticketDetails?.variant_type === "variant") {
        if (result?.data?.data?.id) {
          if (isVariant) {
            setVariantTicketId(result?.data?.data?.id);
            if (isDuplicate) {
              duplicateFormWithPreservedFields();
            } else {
              resetFormWithPreservedFields();
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            navigate("/ticket");
          }
        }
      } else if (ticketDetails?.variant_type === "individual") {
        if (result?.status === 201) {
          navigate("/ticket");
        } else if (result?.status === 500) {
          window.scrollTo({
            top: document.documentElement.scrollHeight / 5,
            behavior: "smooth",
          });
        }
      }
      if  (result?.status === 500) {
        window.scrollTo({
          top: document.documentElement.scrollHeight / 5,
          behavior: "smooth",
        });
      }
    } catch (error) {
      console.error("Error saving ticket:", error);
      Toast().Error("Failed to save ticket");
    }
  };

  const renderPaidTicket = () => {
    if (ticketDetails?.variant_type === "variant") {
      // return ticketNames.map((e, i) => {
      // if (ticketDetails?.variant_name ) {
      // if (ticketDetails?.variant_name?.value ) {
      // if (ticketDetails?.variant_name?.value === e?.value) {
      return (
        <PaidTicket
          variant_type={ticketDetails?.variant_type}
          ticketDetails={ticketDetails[ticketDetails?.variant_name?.value]}
          setticketDetails_={(newDetails) =>
            handleSetTicketDetails(
              ticketDetails?.variant_name?.value,
              newDetails
            )
          }
          ticket_id={ticket_id}
          key={"i"}
          eventDetails={ticketDetails}
          edit={ticket_id ? true : false}
          onVariantSubmit={(e, isDuplicate = false) => handleSaveTicket(e, true, isDuplicate)}
        />
      );
      // }
      // });
    }

    if (ticketDetails?.variant_type === "individual") {
      return (
        <PaidTicket
          ticketDetails={ticketDetails[ticketDetails?.variant_name?.value]}
          // ticketDetails={ticketDetails[ticketDetails?.ticket_name?.value]}
          setticketDetails_={(newDetails) =>
            handleSetTicketDetails(
              ticketDetails?.variant_name?.value,
              newDetails
            )
          }
          ticket_id={ticket_id}
          eventDetails={ticketDetails}
        />
      );
    }

    return null;
  };

  const renderFreeTicket = () => {
    if (ticketDetails?.variant_type === "variant") {
      // return ticketNames.map((e, i) => {
      // if (ticketDetails?.ticket_name?.value === e?.value) {
      return (
        <FreeTicket
          variant_type={ticketDetails?.variant_type}
          ticketDetails={ticketDetails[ticketDetails?.variant_name?.value]}
          setticketDetails_={(newDetails) =>
            handleSetTicketDetails(
              ticketDetails?.variant_name?.value,
              newDetails
            )
          }
          onVariantSubmit={(e, isDuplicate = false) => handleSaveTicket(e, true, isDuplicate)}
          // key={i}
          ticket_id={ticket_id}

        />
      );
      // }
      // });
    }

    if (ticketDetails?.variant_type === "individual") {
      return (
        <FreeTicket
          ticketDetails={ticketDetails[ticketDetails?.variant_name?.value]}
          setticketDetails_={(newDetails) =>
            handleSetTicketDetails(
              ticketDetails?.variant_name?.value,
              newDetails
            )
          }
          ticket_id={ticket_id}
          eventDetails={ticketDetails}

        />
      );
    }

    return null;
  };

  return (
    <>
      <ContainerHeader 
        label={variantTicketId && !edit ? "Add Variant" : edit ? "Edit Ticket" : "Create Ticket"} 
        showAddButton={false} 
      />
      <div className={styles.container}>
        <Tabs_
          selectedIndex={selectedTabIndex}
          setSelectedIndex={handleSetSelectedTabIndex}
          TabList_={[{ label: "Paid Tickets" }, { label: "Free Tickets" }]}
          disabledTabs={ticket_id || variantTicketId ? true : false}
          TabContent_={[
            {
              content: (
                <form
                  onSubmit={(e) => handleSaveTicket(e, false)}
                  key={`paid-form-${formKey}`}
                >
                  <FormFields Fields={formFields} />
                  {renderPaidTicket()}
                </form>
              ),
            },
            {
              content: (
                <form
                  onSubmit={(e) => handleSaveTicket(e, false)}
                  key={`free-form-${formKey}`}
                >
                  <FormFields Fields={formFields} />
                  {renderFreeTicket()}
                </form>
              ),
            },
          ]}
        />
      </div>
    </>
  );
};

export default CreateTicket;